import getSymbolFromCurrency from "currency-symbol-map";

/**
 * Compute the progress percentage from a value between two border values.
 * @param {Number} min The min value of the border.
 * @param {Number} max The max value of the border.
 * @param {Number} value The current value between the borders.
 * @return {Number} The computed percentage position in the interval (integer between 0 and 100).
 */
export const progressPercentage = (min, max, value) => {
  if (min >= max) {
    throw Error("The min value should be lower than max value.");
  } else if (value >= max) {
    return 100;
  } else if (value <= min) {
    return 0;
  }

  return Math.round(((value - min) * 100) / (max - min));
};

/**
 * Compute the price to display from the amount, the currency and the local.
 * @param {number} amount The amount of the price (with a 100 factor). We will divide by 100 to get the real price.
 * @param {number} decimalPlaces The decimal places to display (only for display purpose, not related to the step unit)
 * @param {string} currencySymbol The symbol of the currency to display
 * @param {string} language The language code used to display the price according to the way of the country.
 * @return {string} The price formatted according to the locale and the currency, null if amount is null
 */
export const priceFormat = (amount, decimalPlaces, currencySymbol, language) => {
  if ((!amount && amount !== 0) || Number.isNaN(parseFloat(amount))) {
    return null;
  }
  return new Intl
    .NumberFormat(
      language || "en-GB",
      {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: decimalPlaces || (decimalPlaces !== 0 && 2),
        maximumFractionDigits: decimalPlaces || (decimalPlaces !== 0 && 2),
      },
    )
    .format(amount / 100)
    .replace("€", currencySymbol || "€")
    .replace("EUR", currencySymbol || "EUR");
};

/**
 * Compute the price to display taking in account the currency code and the language
 * @param {number} amount The amount of the price (with a 100 factor). We will divide by 100 to get the real price.
 * @param {number} decimalPlaces The decimal places to display
 * @param {string} currencyCode The trigram of the currency, e.g "EUR"
 * @param {string} language The language
 * @param {boolean} useNarrowSymbol Whether we want display the local currency symbol whatever the language
 * @return {string} The price formatted according to the locale and the currency
*/
export const intlPriceFormat = (amount, decimalPlaces, currencyCode, language, useNarrowSymbol = false) => {
  if ((!amount && amount !== 0) || Number.isNaN(parseFloat(amount)) || !currencyCode) {
    return null;
  }

  const currencySymbol = getSymbolFromCurrency(currencyCode);

  if (useNarrowSymbol && currencySymbol) {
    return new Intl
      .NumberFormat(
        language || "en-GB",
        {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: decimalPlaces || (decimalPlaces !== 0 && 2),
          maximumFractionDigits: decimalPlaces || (decimalPlaces !== 0 && 2),
        },
      )
      .format(amount / 100)
      .replace("€", currencySymbol)
      .replace("EUR", currencySymbol);
  }

  return new Intl
    .NumberFormat(
      language || "en-GB",
      {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: decimalPlaces || (decimalPlaces !== 0 && 2),
        maximumFractionDigits: decimalPlaces || (decimalPlaces !== 0 && 2),
      },
    )
    .format(amount / 100);
};

/**
 * Compute the conversion of an amount expressed in a virtual currency to the real currency
 * using the exchange rate's numerator and denominator
 * @param {number} amount The amount expressed in the virtual currency
 * @param {Object} exchangeRate The exchange rate to be used for performing the conversion
 * @param {number} exchangeRate.numerator The comparability for the real currency
 * @param {number} exchangeRate.denominator The comparability for the virtual currency
 * @return {number} The converted amount
*/
export const virtualToRealAmountConverter = (amount, exchangeRate) => {
  let convertedAmount = amount * exchangeRate.numerator;
  convertedAmount /= exchangeRate.denominator;
  convertedAmount = Math.round(convertedAmount);
  return convertedAmount;
};

/**
 * Compute the conversion of an amount expressed in a real currency to a virtual currency
 * using the exchange rate's numerator and denominator
 * @param {number} amount The amount expressed in the real currency
 * @param {Object} exchangeRate The exchange rate to be used for performing the conversion
 * @param {number} exchangeRate.numerator The comparability for the real currency
 * @param {number} exchangeRate.denominator The comparability for the virtual currency
 * @return {number} The converted amount
*/
export const realToVirtualAmountConverter = (amount, exchangeRate) => {
  let convertedAmount = amount * exchangeRate.denominator;
  convertedAmount /= exchangeRate.numerator;
  convertedAmount = Math.round(convertedAmount);
  return convertedAmount;
};

/**
 * Compute a size value from 'rem' unit to 'px'.
 * @param remValue The size value in 'rem' to convert in 'px'.
 * @return {number} The converted size value in 'px'.
 */
export const remToPx = (remValue) => {
  if (!remValue) {
    return 0;
  }
  const remCleanValue = parseFloat(remValue.toString().split("rem")[0]);
  const baseFontSize = parseFloat(getComputedStyle(document.documentElement)?.fontSize || 16);
  return remCleanValue * baseFontSize;
};

/**
 * Compute a size value from 'px' unit to 'rem'.
 * @param pxValue The size value in 'px' to convert in 'rem'.
 * @return {number} The converted size value in 'rem'.
 */
export const pxToRem = (pxValue) => {
  if (!pxValue) {
    return 0;
  }
  const pxCleanValue = parseFloat(pxValue.toString().split("px")[0]);
  const baseFontSize = parseFloat(getComputedStyle(document.documentElement)?.fontSize || 16);
  return pxCleanValue / baseFontSize;
};
